import { createTheme, alpha } from '@mui/material/styles';

// ---------------------------------------------------------------------------
// Skyline design system — "Horizon"
// Brand orange on deep-navy glass, slate neutrals, Sora + Inter type.
// Exported as a factory so the app can switch between light and dark modes.
// ---------------------------------------------------------------------------

export const BRAND = {
  orange: '#E03F00',
  orangeBright: '#FF5C1F',
  orangeSoft: '#FF7A45',
  navy: '#0F172A',
  navyLight: '#1E293B',
};

const FONT_BODY = '"Inter", "Helvetica Neue", Arial, sans-serif';
const FONT_DISPLAY = '"Sora", "Inter", "Helvetica Neue", Arial, sans-serif';

const gradients = {
  brand: `linear-gradient(135deg, ${BRAND.orangeBright} 0%, ${BRAND.orange} 55%, #C93800 100%)`,
  brandSoft: `linear-gradient(135deg, ${alpha(BRAND.orangeBright, 0.14)} 0%, ${alpha(
    BRAND.orange,
    0.05
  )} 100%)`,
  navy: 'linear-gradient(180deg, #14203A 0%, #0F172A 55%, #0B1120 100%)',
  night: 'radial-gradient(1200px 500px at 15% -10%, rgba(255, 92, 31, 0.16), transparent 60%), linear-gradient(180deg, #14203A 0%, #0F172A 55%, #0B1120 100%)',
};

const lightPalette = {
  mode: 'light',
  primary: {
    main: BRAND.orange,
    light: BRAND.orangeSoft,
    dark: '#B53300',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#1E293B',
    light: '#334155',
    dark: '#0F172A',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#F3F5FA',
    paper: '#FFFFFF',
  },
  text: {
    primary: '#0F172A',
    secondary: '#5B6B84',
    disabled: '#9AA7BC',
  },
  divider: alpha('#0F172A', 0.09),
  success: { main: '#118D57', light: '#3FC79A', dark: '#0B6941', contrastText: '#fff' },
  warning: { main: '#B76E00', light: '#FFAB00', dark: '#7A4100', contrastText: '#fff' },
  error: { main: '#D32030', light: '#FF5630', dark: '#B71D18', contrastText: '#fff' },
  info: { main: '#006C9C', light: '#00B8D9', dark: '#003768', contrastText: '#fff' },
  action: {
    hover: alpha('#0F172A', 0.04),
    selected: alpha(BRAND.orange, 0.1),
    focus: alpha(BRAND.orange, 0.16),
  },
};

const darkPalette = {
  mode: 'dark',
  primary: {
    main: BRAND.orangeBright,
    light: '#FF8A5C',
    dark: '#D63C00',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#94A3B8',
    light: '#CBD5E1',
    dark: '#64748B',
    contrastText: '#0F172A',
  },
  background: {
    default: '#0B1120',
    paper: '#111B2E',
  },
  text: {
    primary: '#F1F5F9',
    secondary: '#94A3B8',
    disabled: '#5B6B84',
  },
  divider: alpha('#94A3B8', 0.14),
  success: { main: '#3FC79A', light: '#5BE49B', dark: '#118D57', contrastText: '#0F172A' },
  warning: { main: '#FFAB00', light: '#FFD666', dark: '#B76E00', contrastText: '#0F172A' },
  error: { main: '#FF5630', light: '#FFAC82', dark: '#B71D18', contrastText: '#fff' },
  info: { main: '#00B8D9', light: '#61F3F3', dark: '#006C9C', contrastText: '#0F172A' },
  action: {
    hover: alpha('#94A3B8', 0.08),
    selected: alpha(BRAND.orangeBright, 0.16),
    focus: alpha(BRAND.orangeBright, 0.24),
  },
};

export const getTheme = (mode = 'light') => {
  const isLight = mode === 'light';
  const palette = isLight ? lightPalette : darkPalette;

  const softShadow = isLight
    ? '0 1px 2px rgba(15, 23, 42, 0.06), 0 8px 24px -6px rgba(15, 23, 42, 0.10)'
    : '0 1px 2px rgba(0, 0, 0, 0.4), 0 10px 30px -8px rgba(0, 0, 0, 0.55)';
  const hoverShadow = isLight
    ? '0 2px 4px rgba(15, 23, 42, 0.07), 0 16px 32px -8px rgba(15, 23, 42, 0.16)'
    : '0 2px 4px rgba(0, 0, 0, 0.5), 0 18px 40px -10px rgba(0, 0, 0, 0.65)';
  const cardBorder = isLight ? alpha('#0F172A', 0.06) : alpha('#94A3B8', 0.12);

  const theme = createTheme({
    palette,
    shape: { borderRadius: 12 },
    custom: {
      gradients,
      softShadow,
      hoverShadow,
      cardBorder,
      glow: `0 8px 24px -6px ${alpha(BRAND.orange, 0.45)}`,
      appBarHeight: 64,
      bottomNavHeight: 68,
      drawerWidth: 264,
      drawerWidthCollapsed: 84,
    },
    typography: {
      fontFamily: FONT_BODY,
      h1: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(2rem, 5vw, 2.75rem)', letterSpacing: '-0.02em', lineHeight: 1.15 },
      h2: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 'clamp(1.7rem, 4vw, 2.25rem)', letterSpacing: '-0.02em', lineHeight: 1.2 },
      h3: { fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 'clamp(1.45rem, 3vw, 1.85rem)', letterSpacing: '-0.015em', lineHeight: 1.25 },
      h4: { fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 'clamp(1.3rem, 2.5vw, 1.5rem)', letterSpacing: '-0.01em', lineHeight: 1.3 },
      h5: { fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: '1.125rem', letterSpacing: '-0.005em', lineHeight: 1.4 },
      h6: { fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: '1rem', lineHeight: 1.45 },
      subtitle1: { fontWeight: 600, fontSize: '0.9375rem', lineHeight: 1.5 },
      subtitle2: { fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.5 },
      body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
      body2: { fontSize: '0.875rem', lineHeight: 1.55 },
      caption: { fontSize: '0.75rem', lineHeight: 1.5 },
      overline: { fontWeight: 700, fontSize: '0.6875rem', letterSpacing: '0.08em', lineHeight: 1.6 },
      button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.01em' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' },
          body: {
            backgroundColor: palette.background.default,
            minHeight: '100dvh',
          },
          '#root': { minHeight: '100dvh' },
          '::selection': {
            backgroundColor: alpha(BRAND.orange, 0.25),
          },
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: `${alpha(isLight ? '#0F172A' : '#94A3B8', 0.28)} transparent`,
          },
          '*::-webkit-scrollbar': { width: 8, height: 8 },
          '*::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: alpha(isLight ? '#0F172A' : '#94A3B8', 0.24),
          },
          '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: alpha(isLight ? '#0F172A' : '#94A3B8', 0.4),
          },
          '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.01ms !important',
            },
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 10,
            minHeight: 40,
            paddingInline: 18,
            transition: 'transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease',
            '&:active': { transform: 'scale(0.98)' },
          },
          sizeLarge: { minHeight: 48, fontSize: '0.9375rem' },
          sizeSmall: { minHeight: 34, paddingInline: 12 },
          containedPrimary: {
            backgroundImage: gradients.brand,
            boxShadow: `0 6px 16px -4px ${alpha(BRAND.orange, 0.45)}`,
            '&:hover': {
              backgroundImage: gradients.brand,
              filter: 'brightness(1.06)',
              boxShadow: `0 10px 22px -6px ${alpha(BRAND.orange, 0.55)}`,
            },
            '&.Mui-disabled': { backgroundImage: 'none' },
          },
          containedSecondary: {
            backgroundColor: isLight ? BRAND.navy : '#E2E8F0',
            color: isLight ? '#fff' : BRAND.navy,
            '&:hover': { backgroundColor: isLight ? '#1E293B' : '#CBD5E1' },
          },
          outlined: {
            borderColor: isLight ? alpha('#0F172A', 0.16) : alpha('#94A3B8', 0.28),
            '&:hover': {
              borderColor: palette.primary.main,
              backgroundColor: alpha(palette.primary.main, 0.05),
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            transition: 'background-color 160ms ease, transform 160ms ease',
            '&:active': { transform: 'scale(0.94)' },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          rounded: { borderRadius: 16 },
          elevation1: { boxShadow: softShadow },
          elevation2: { boxShadow: softShadow },
          elevation3: { boxShadow: hoverShadow },
          elevation4: { boxShadow: hoverShadow },
          outlined: { borderColor: cardBorder },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: `1px solid ${cardBorder}`,
            boxShadow: softShadow,
            backgroundImage: 'none',
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: { padding: '20px 20px 12px' },
          title: { fontSize: '1rem', fontWeight: 600, fontFamily: FONT_DISPLAY },
        },
      },
      MuiCardContent: {
        styleOverrides: { root: { padding: 20, '&:last-child': { paddingBottom: 20 } } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: isLight ? '#FFFFFF' : alpha('#94A3B8', 0.06),
            transition: 'box-shadow 160ms ease',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isLight ? alpha('#0F172A', 0.14) : alpha('#94A3B8', 0.24),
              transition: 'border-color 160ms ease',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isLight ? alpha('#0F172A', 0.32) : alpha('#94A3B8', 0.45),
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 4px ${alpha(palette.primary.main, 0.12)}`,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: palette.primary.main,
                borderWidth: 1.5,
              },
            },
          },
          input: {
            '&::placeholder': { color: palette.text.disabled, opacity: 1 },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { fontSize: '0.9375rem' },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 8 },
          sizeSmall: { height: 24, fontSize: '0.75rem' },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 20,
            border: `1px solid ${cardBorder}`,
            boxShadow: hoverShadow,
            backgroundImage: 'none',
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: alpha('#0B1120', isLight ? 0.55 : 0.7),
            backdropFilter: 'blur(4px)',
            '&.MuiBackdrop-invisible': {
              backgroundColor: 'transparent',
              backdropFilter: 'none',
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: '1.125rem' },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            border: `1px solid ${cardBorder}`,
            boxShadow: hoverShadow,
            marginTop: 6,
          },
          list: { padding: 6 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontSize: '0.875rem',
            fontWeight: 500,
            minHeight: 40,
            '&:hover': { backgroundColor: palette.action.hover },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: { borderRadius: 10 },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: { borderRadius: 16 },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: isLight ? '#F8FAFD' : alpha('#94A3B8', 0.06),
              color: palette.text.secondary,
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              borderBottom: `1px solid ${palette.divider}`,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${palette.divider}`, padding: '14px 16px' },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: palette.action.hover },
            '&:last-child .MuiTableCell-root': { borderBottom: 0 },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 44 },
          indicator: { height: 3, borderRadius: 3, backgroundImage: gradients.brand },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 44,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '0.875rem',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 12, fontWeight: 500 },
          standardSuccess: { backgroundColor: alpha(palette.success.main, isLight ? 0.12 : 0.18), color: isLight ? palette.success.dark : palette.success.light },
          standardError: { backgroundColor: alpha(palette.error.main, isLight ? 0.1 : 0.18), color: isLight ? palette.error.dark : palette.error.light },
          standardWarning: { backgroundColor: alpha(palette.warning.main, isLight ? 0.12 : 0.18), color: isLight ? palette.warning.dark : palette.warning.light },
          standardInfo: { backgroundColor: alpha(palette.info.main, isLight ? 0.1 : 0.18), color: isLight ? palette.info.dark : palette.info.light },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isLight ? BRAND.navy : '#E2E8F0',
            color: isLight ? '#fff' : BRAND.navy,
            borderRadius: 8,
            fontSize: '0.75rem',
            fontWeight: 500,
            padding: '6px 10px',
          },
          arrow: { color: isLight ? BRAND.navy : '#E2E8F0' },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: { fontWeight: 600 },
        },
      },
      MuiSkeleton: {
        defaultProps: { animation: 'wave' },
        styleOverrides: { root: { borderRadius: 8 } },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 8, height: 6, backgroundColor: alpha(palette.primary.main, 0.12) },
          bar: { borderRadius: 8 },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { backgroundImage: 'none', border: 'none' },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: 'none',
            borderRadius: 16,
            backgroundColor: palette.background.paper,
            fontSize: '0.875rem',
            '--DataGrid-rowBorderColor': palette.divider,
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: isLight ? '#F8FAFD' : alpha('#94A3B8', 0.06),
              borderBottom: `1px solid ${palette.divider}`,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: palette.text.secondary,
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            },
            '& .MuiDataGrid-cell': {
              borderTop: `1px solid ${palette.divider}`,
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
            '& .MuiDataGrid-row:hover': { backgroundColor: palette.action.hover },
            '& .MuiDataGrid-footerContainer': { borderTop: `1px solid ${palette.divider}` },
            '& .MuiDataGrid-toolbarContainer': {
              padding: 12,
              gap: 8,
              '& .MuiButton-root': { color: palette.text.secondary },
            },
          },
        },
      },
    },
  });

  return theme;
};

// Default light theme kept for backwards compatibility with existing imports.
const theme = getTheme('light');

export default theme;
